import React, { useState } from 'react';
import axios from 'axios';
import type { Firma } from './FirmaSection';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

type Art = 'kopf' | 'fuss' | 'logo';

type GrafikFeld = 'briefpapier_kopf' | 'briefpapier_fuss' | 'logo_pfad';

const ARTEN: Array<{ id: Art; label: string; feld: GrafikFeld; hinweis: string }> = [
  {
    id: 'kopf', label: 'Kopfgrafik', feld: 'briefpapier_kopf',
    hinweis: 'Ersetzt den aus den Stammdaten gerenderten Briefkopf (Firmenname, Adresse, '
      + 'Kontaktdaten) vollständig durch dieses Bild.',
  },
  {
    id: 'fuss', label: 'Fußgrafik', feld: 'briefpapier_fuss',
    hinweis: 'Ersetzt die aus den Stammdaten gerenderte Fußzeile (Bank, Registergericht, '
      + 'Geschäftsführer) vollständig durch dieses Bild.',
  },
  {
    id: 'logo', label: 'Logo', feld: 'logo_pfad',
    hinweis: 'Wird zusätzlich zum Kopf platziert — unabhängig davon, ob eine eigene '
      + 'Kopfgrafik hinterlegt ist.',
  },
];

const ERLAUBTE_ENDUNGEN = '.png,.jpg,.jpeg,.svg';

interface Props {
  firma: Firma;
  onFirmaChange: (f: Firma) => void;
  onFehler: (m: string | null) => void;
}

/** Letzter Pfadbestandteil, unabhängig davon ob der Server mit / oder \ trennt. */
const dateiname = (pfad: string | null): string | null => {
  if (!pfad) return null;
  const teile = pfad.split(/[\\/]/);
  return teile[teile.length - 1] || pfad;
};

/**
 * Briefpapier-Reiter der Firma-Sektion (`/api/firma/briefpapier`, Task 6-API).
 * Es gibt bewusst keine Bildvorschau: die API liefert nur Upload/Delete,
 * keine Route zum Abrufen der hochgeladenen Datei (nur der Pfad steckt im
 * Firma-Objekt) — eine Vorschau wäre eine eigene Backend-Route.
 */
const FirmaBriefpapier: React.FC<Props> = ({ firma, onFirmaChange, onFehler }) => {
  const [hochladenArt, setHochladenArt] = useState<Art | null>(null);

  const hochladen = async (art: Art, datei: File) => {
    onFehler(null);
    setHochladenArt(art);
    const form = new FormData();
    form.append('art', art);
    form.append('datei', datei);
    try {
      const r = await axios.post<Firma>(`${API_URL}/api/firma/briefpapier`, form);
      onFirmaChange(r.data);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      onFehler(`Upload fehlgeschlagen: ${msg}`);
    } finally {
      setHochladenArt(null);
    }
  };

  const entfernen = async (art: Art, label: string) => {
    if (!window.confirm(`${label} entfernen? Der Beleg zeigt danach wieder die aus den Stammdaten gerenderte Fassung.`)) {
      return;
    }
    onFehler(null);
    try {
      const r = await axios.delete<Firma>(`${API_URL}/api/firma/briefpapier/${art}`);
      onFirmaChange(r.data);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      onFehler(`Entfernen fehlgeschlagen: ${msg}`);
    }
  };

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Liegt eine Kopf- oder Fußgrafik vor, ersetzt sie den aus den Stammdaten gerenderten
        Bereich vollständig — die zugehörigen Textfelder werden dann nicht mehr gedruckt. Ohne
        Grafik wird aus den Stammdaten-Feldern gerendert. Erlaubte Formate: PNG, JPG, SVG.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {ARTEN.map(a => {
          const pfad = firma[a.feld];
          const laedt = hochladenArt === a.id;
          return (
            <div key={a.id} style={{ border: '1px solid var(--bone-3)', padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                <strong style={{ fontSize: 14 }}>{a.label}</strong>
                <span style={{ fontSize: 12, color: pfad ? 'var(--green-500)' : 'var(--ink-2)' }}>
                  {pfad ? `Hinterlegt: ${dateiname(pfad)}` : 'Kein Bild hinterlegt'}
                </span>
              </div>
              <p style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 0, marginBottom: 10 }}>
                {a.hinweis}
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="file"
                  accept={ERLAUBTE_ENDUNGEN}
                  disabled={laedt}
                  onChange={e => {
                    const datei = e.target.files?.[0];
                    e.target.value = '';
                    if (datei) hochladen(a.id, datei);
                  }}
                />
                {pfad && (
                  <button type="button" className="k-btn k-btn--danger k-btn--sm" disabled={laedt} onClick={() => entfernen(a.id, a.label)}>
                    Entfernen
                  </button>
                )}
                {laedt && <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>Lädt hoch …</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FirmaBriefpapier;
